import { cn } from '@wellesley/ui'
import { ToastT } from 'sonner'
import { useDispatch, useSelector } from 'react-redux'
import { FC, useEffect, useRef, useState } from 'react'

import getToastIcon from '../helpers/getToastIcon'
import { type ToastProps } from '../types'
import { selectToastSize, addToastSize, removeToastSize } from '../slice'
import { getIconVariant } from '../helpers/getIconVariant'
import escapeHtml from 'helpers/escapeHtml'

const TOAST_TITLE_TAG_WHITELIST: Array<keyof HTMLElementTagNameMap> = [
  'b',
  'strong',
  'i',
  'em',
  'u',
  's',
  'p',
  'br',
  'a',
]

const SystemToastMessage: FC<ToastProps> = (props) => {
  const {
    title,
    actionButton,
    id,
    type,
    promise,
    dismiss,
    dataTestId = 'toast-container',
    customIconRsc,
    iconVariant,
    isIcon = true,
  } = props
  const dispatch = useDispatch()
  const minToastSize = useSelector(selectToastSize)

  const [toastType, setToastType] = useState<ToastT['type']>(type)
  const [isLoading, setIsLoading] = useState(type === 'loading')
  const ref = useRef<HTMLDivElement>(null)

  const toastIcon =
    (customIconRsc && <img src={customIconRsc} alt="custom icon" />) ||
    (iconVariant && getIconVariant(iconVariant)) ||
    getToastIcon(isLoading ? 'loading' : toastType)

  useEffect(() => {
    dispatch(addToastSize({ id: `${id}`, size: ref.current?.offsetWidth ?? 0 }))
  }, [isLoading])

  useEffect(
    () => () => {
      dispatch(removeToastSize(`${id}`))
    },
    [],
  )

  useEffect(() => {
    if (!promise) return

    let cancelled = false

    setIsLoading(true)

    void promise
      .then(() => setToastType('success'))
      .catch(() => setToastType('error'))
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [promise])

  const handleNotificationClick = () => {
    if (!actionButton) dismiss(id)
  }

  const safeTitle = escapeHtml(title, {
    whiteList: TOAST_TITLE_TAG_WHITELIST,
  })

  return (
    <div
      ref={ref}
      onClick={handleNotificationClick}
      style={{ minWidth: minToastSize }}
      data-testid={dataTestId}
      className={cn(
        'gap-xs px-xs flex min-h-14 w-full items-center rounded-lg bg-gray-800',
        {
          'bg-red-500': toastType === 'error',
          'border border-gray-700': toastType === 'success',
          'cursor-pointer': !actionButton,
        },
      )}
    >
      {isIcon && toastIcon}
      <p
        dangerouslySetInnerHTML={{ __html: safeTitle }}
        className="text-base font-medium text-white"
      />
      {actionButton && (
        <button
          className="h-s px-xs rounded-sm bg-gray-500 text-sm"
          onClick={() => {
            actionButton.onClick()
            dismiss(id)
          }}
        >
          {actionButton.label}
        </button>
      )}
    </div>
  )
}

export default SystemToastMessage
